import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { ordersAPI } from '../../services/api';
import {
  ClockIcon,
  CheckCircleIcon,
  MapPinIcon,
  StarIcon,
  ChartBarIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const JobHistory: React.FC = () => {
  const { user } = useAuth();
  const [completedJobs, setCompletedJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalCompleted: 0,
    avgRating: 5.0,
    totalEarnings: 0,
    totalDistance: 0
  });

  useEffect(() => {
    fetchJobHistory();
  }, []);

  const fetchJobHistory = async () => {
    try {
      setLoading(true);
      const ordersData = await ordersAPI.getUserOrders();
      const completed = (ordersData || []).filter((order: any) => 
        order.status === 'delivered'
      );
      
      setCompletedJobs(completed);
      setStats({
        totalCompleted: completed.length,
        avgRating: user?.average_rating || 5.0,
        totalEarnings: completed.reduce((sum: number, order: any) => sum + Number(order.total_price ?? 0), 0),
        totalDistance: completed.length * 5.2
      });
    } catch (error) {
      console.error('Error fetching job history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-volexx-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Job History
          </h1>
          <p className="mt-2 text-gray-600">View your completed deliveries and performance</p>
        </div>

        {/* Performance Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-volexx shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Completed</p>
                <p className="text-3xl font-bold text-volexx-blue-600 mt-1">{stats.totalCompleted}</p>
              </div>
              <CheckCircleIcon className="h-10 w-10 text-volexx-blue-600 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-volexx shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Average Rating</p>
                <div className="flex items-center mt-1">
                  <p className="text-3xl font-bold text-yellow-500">{stats.avgRating}</p>
                  <StarIconSolid className="h-6 w-6 text-yellow-500 ml-2" />
                </div>
              </div>
              <StarIcon className="h-10 w-10 text-yellow-500 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-volexx shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Earnings</p>
                <p className="text-3xl font-bold text-green-600 mt-1">
                  ${stats.totalEarnings.toFixed(2)}
                </p>
              </div>
              <ChartBarIcon className="h-10 w-10 text-green-600 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-volexx shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Distance Covered</p>
                <p className="text-3xl font-bold text-volexx-orange-600 mt-1">
                  {stats.totalDistance.toFixed(1)} km
                </p>
              </div>
              <MapPinIcon className="h-10 w-10 text-volexx-orange-600 opacity-50" />
            </div>
          </div>
        </div>

        {/* Completed Jobs List */}
        <div className="bg-white rounded-volexx shadow-volexx border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Completed Deliveries</h2>
          </div>
          <div className="divide-y divide-gray-100">
            {completedJobs.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No completed jobs yet</p>
                <p className="text-sm text-gray-400 mt-1">Your delivery history will appear here</p>
              </div>
            ) : (
              completedJobs.map((job) => (
                <div key={job.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <CheckCircleIcon className="h-6 w-6 text-green-500 mt-1" />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-volexx">
                            Completed
                          </span>
                          <p className="text-xs text-gray-500">
                            <CalendarIcon className="h-3 w-3 inline mr-1" />
                            {new Date(job.created_at || Date.now()).toLocaleDateString()}
                          </p>
                        </div>
                        <p className="text-sm font-medium text-gray-900 mb-1">
                          <MapPinIcon className="h-4 w-4 inline mr-1 text-gray-400" />
                          {job.pickup_location}
                        </p>
                        <p className="text-sm text-gray-600">→ {job.dropoff_location}</p>
                        <div className="flex items-center mt-2">
                          <div className="flex items-center">
                            {[...Array(5)].map((_, i) => (
                              <StarIconSolid
                                key={i}
                                className={`h-4 w-4 ${i < 4 ? 'text-yellow-500' : 'text-gray-300'}`}
                              />
                            ))}
                          </div>
                          <span className="ml-2 text-xs text-gray-500">4.0</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-lg font-bold text-green-600">
                        ${Number(job.total_price ?? 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{(job as any).vehicle_type || 'Vehicle'}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobHistory;
